import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { Badge } from '@autop/ui/src/badge';

import { SurveyBanner } from '@/widgets/survey-banner';
import { AssetDownloadButton } from '@/features/download-asset';
import { assetMetaOf, type LibraryAsset } from '@/entities/asset';
import {
  formatPublishedAt,
  type Video,
  youtubeThumbnailUrl,
  youtubeWatchUrl,
} from '@/entities/video';

function AssetRow({
  asset,
  resumePath,
}: {
  asset: LibraryAsset;
  resumePath: string;
}): ReactElement {
  return (
    <li className="flex flex-wrap items-center gap-3 border-border border-t p-4">
      <Badge variant="mint">무료</Badge>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[14.5px] font-medium">{asset.title}</span>
        <span className="text-[12.5px] text-muted-foreground">{assetMetaOf(asset)}</span>
      </div>
      <AssetDownloadButton
        assetId={asset.id}
        resumePath={resumePath}
        isLink={asset.format === 'LINK'}
      />
    </li>
  );
}

/**
 * 영상 한 편과 그 영상의 자료들.
 *
 * `id` 를 영상 id 로 둬서 유튜브 설명란이 `/library#v1` 로 바로 꽂을 수 있게 한다.
 * 자료를 찾으러 온 사람을 목록 맨 위에 떨어뜨리면 자기 영상을 다시 찾아야 한다.
 */
export function VideoAssetGroup({ video }: { video: Video }): ReactElement {
  const detailPath = `/library/${video.slug}`;
  // 챕터 순서대로 먼저, 미배치 자료는 뒤에 — 상세 페이지의 구간 순서와 같은 축이다
  const assets = [
    ...video.chapters.flatMap((chapter) => chapter.assets),
    ...video.unassignedAssets,
  ];

  return (
    <article id={video.id} className="overflow-hidden rounded-lg border border-border scroll-mt-20">
      <div className="grid gap-5 p-5 sm:grid-cols-[14rem_minmax(0,1fr)] sm:items-start">
        <Link
          to={detailPath}
          className="relative block aspect-video overflow-hidden rounded-md bg-secondary"
        >
          <img
            src={youtubeThumbnailUrl(video.youtubeId)}
            alt=""
            loading="lazy"
            className="size-full object-cover"
          />
        </Link>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
            <span data-numeric="" className="font-mono">
              {formatPublishedAt(video.publishedAt)}
            </span>
          </div>
          <h3 className="text-[1.0625rem] font-semibold tracking-[-0.03em]">
            <Link to={detailPath} className="underline-offset-4 hover:underline">
              {video.title}
            </Link>
          </h3>
          {/* 운영자가 소개를 줄 바꿔 쓴다 — 줄바꿈만 살리고 연속 공백은 합친다 */}
          <p className="whitespace-pre-line text-[13.5px] leading-[1.8] text-muted-foreground">
            {video.summary}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to={detailPath} className="text-[13px] underline underline-offset-4">
              구간별 정리 보기
            </Link>
            <a
              href={youtubeWatchUrl(video.youtubeId)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[13px] text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              유튜브에서 보기 ↗
            </a>
          </div>
        </div>
      </div>

      {/* 자료 칸 바로 위 — 자료를 받으러 온 사람이 반드시 지나가는 자리다 */}
      <SurveyBanner youtubeId={video.youtubeId} variant="band" />

      {assets.length === 0 ? (
        <p className="border-border border-t p-4 text-[13px] text-muted-foreground">
          이 영상의 자료는 준비 중입니다.
        </p>
      ) : (
        <>
          <p className="border-border border-t bg-secondary/40 px-4 py-2 text-[12px] font-medium text-muted-foreground">
            이 영상의 자료 {video.assetCount}개
          </p>
          <ul>
            {assets.map((asset) => (
              <AssetRow key={asset.id} asset={asset} resumePath={detailPath} />
            ))}
          </ul>
        </>
      )}
    </article>
  );
}
